// Shared building blocks for the auth screens (sign in / sign up / forgot /
// check-email), recreated from the design handoff. Kept framework-light —
// plain elements styled by auth.css — so the same markup works in all three
// pages.

import { useState } from 'react';
// Bundled brand mark (same asset the nav rail uses).
import rrLogo from '../components/AppNav/icons/rr-logo.png';

export const RR_LOGO_URL: string = rrLogo;

// ---- Icons (inline stroke SVGs, ~2px weight per the handoff) ----
export const ArrowIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);
export const BackIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
export const MailIcon: React.FC = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);
export const InfoIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);
export const AlertIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

export interface BrandStat {
  num: string;
  label: string;
}

// Deep-teal editorial panel shown on the left of the split (sign in / up).
export const BrandPanel: React.FC<{
  eyebrow: string;
  title: React.ReactNode;
  quote?: string;
  cite?: string;
  stats?: BrandStat[];
}> = ({ eyebrow, title, quote, cite, stats }) => (
  <aside className="auth-brand">
    <div className="auth-brand-mark">
      <span className="auth-brand-logo">
        <img src={RR_LOGO_URL} alt="Rollover Rep" />
      </span>
      <div>
        <div>rollover rep</div>
        <span className="auth-brand-sub">Admissions OS</span>
      </div>
    </div>

    <div className="auth-brand-body">
      <div className="auth-eyebrow auth-brand-eyebrow">{eyebrow}</div>
      <h1>{title}</h1>
      {quote && (
        <blockquote className="auth-brand-quote">
          {quote}
          {cite && <cite>{cite}</cite>}
        </blockquote>
      )}
      {stats && (
        <div className="auth-stats">
          {stats.map((s) => (
            <div key={s.label}>
              <div className="auth-stat-num">{s.num}</div>
              <div className="auth-stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      )}
    </div>

    <div className="auth-brand-foot">
      <span>© 2026 Rollover Rep</span>
      <a href="https://rolloverrep.com/privacy" target="_blank" rel="noreferrer">Privacy</a>
      <a href="https://rolloverrep.com/terms" target="_blank" rel="noreferrer">Terms</a>
    </div>
  </aside>
);

// Labeled field wrapper with an optional right-aligned hint (e.g. "Forgot?").
export const Field: React.FC<{
  label: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}> = ({ label, hint, children }) => (
  <div className="auth-field">
    <label className="auth-field-label">
      <span>{label}</span>
      {hint && <span className="auth-field-hint">{hint}</span>}
    </label>
    {children}
  </div>
);

// Controlled text input styled as .auth-input.
export const TextInput: React.FC<{
  type?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoComplete?: string;
}> = ({ type = 'text', value, onChange, placeholder, autoComplete }) => (
  <div className="auth-input">
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoComplete={autoComplete}
    />
  </div>
);

// Password input with a Show/Hide toggle.
export const PasswordInput: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoComplete?: string;
}> = ({ value, onChange, placeholder = '••••••••', autoComplete }) => {
  const [shown, setShown] = useState(false);
  return (
    <div className="auth-input auth-input--with-toggle">
      <input
        type={shown ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
      />
      <button type="button" className="auth-input-toggle" onClick={() => setShown((s) => !s)}>
        {shown ? 'Hide' : 'Show'}
      </button>
    </div>
  );
};

// Password strength scoring (0–4): length, upper/lower mix, digit, symbol.
export function passwordScore(pw: string): number {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= 8) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s;
}
// A password meets the sign-up rule when it satisfies all four criteria.
export function passwordMeetsRule(pw: string): boolean {
  return passwordScore(pw) >= 4;
}

const STRENGTH_LABEL = ['Too short', 'Weak', 'Fair', 'Strong', 'Very strong'];

export const PasswordMeter: React.FC<{ pw: string }> = ({ pw }) => {
  const score = passwordScore(pw);
  return (
    <>
      <div className="auth-meter" data-strength={score}>
        <span /><span /><span /><span />
      </div>
      <div className="auth-meter-label">
        Strength: <strong>{STRENGTH_LABEL[score]}</strong> · 8+ characters, mix of upper/lowercase, a number and symbol.
      </div>
    </>
  );
};

// Simple email format guard for client-side validation.
export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}
